import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Loader2, Play, VideoOff } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { getVideos, createVideo, deleteVideo } from '../lib/api';
import { toast } from 'sonner';

const VideoManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ video_type: 'youtube', video_url: '', title: '', description: '' });

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getVideos());
    } catch (e) {
      toast.error('Error al cargar videos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.video_url || !form.title) {
      toast.error('Completa la URL y el título del video');
      return;
    }
    setSaving(true);
    try {
      await createVideo({ ...form, order: items.length });
      toast.success('Video agregado');
      setForm({ video_type: 'youtube', video_url: '', title: '', description: '' });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error('Error al agregar video');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este video?')) return;
    try {
      await deleteVideo(id);
      toast.success('Video eliminado');
      setItems(items.filter((i) => i.id !== id));
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Videos</h2>
          <p className="text-white/60 text-sm">{items.length} videos publicados</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          data-testid="add-video-button"
          className="bg-[#60A5FA] hover:bg-[#3B82F6] text-white rounded-xl"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar video
        </Button>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} className="bg-[#0F1D33]/60 border border-white/10 rounded-2xl p-6 mb-6 space-y-4">
          <div>
            <label className="text-white/70 text-sm mb-2 block">Plataforma</label>
            <select
              value={form.video_type}
              onChange={(e) => setForm({ ...form, video_type: e.target.value })}
              data-testid="video-type"
              className="w-full h-10 bg-[#0A1628]/60 border border-white/20 text-white rounded-xl px-3"
            >
              <option value="youtube">YouTube</option>
              <option value="instagram">Instagram Reels</option>
              <option value="tiktok">TikTok</option>
              <option value="mp4">MP4 (URL directa)</option>
            </select>
          </div>
          <div>
            <label className="text-white/70 text-sm mb-2 block">URL del video</label>
            <Input
              value={form.video_url}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder="https://youtube.com/watch?v=... o URL del MP4"
              data-testid="video-url"
              className="bg-[#0A1628]/60 border-white/20 text-white rounded-xl"
            />
            <p className="text-white/40 text-xs mt-1">
              Pega el enlace completo del video (YouTube, Instagram, TikTok o archivo MP4)
            </p>
          </div>
          <div>
            <label className="text-white/70 text-sm mb-2 block">Título</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ej: Proceso de pulido profesional"
              data-testid="video-title"
              className="bg-[#0A1628]/60 border-white/20 text-white rounded-xl"
            />
          </div>
          <div>
            <label className="text-white/70 text-sm mb-2 block">Descripción (opcional)</label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              data-testid="video-description"
              className="bg-[#0A1628]/60 border-white/20 text-white rounded-xl resize-none"
            />
          </div>
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} data-testid="save-video-button" className="bg-[#60A5FA] hover:bg-[#3B82F6] text-white rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-white/20 text-white rounded-xl">
              Cancelar
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#60A5FA] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-white/40">
          <VideoOff className="w-12 h-12 mx-auto mb-4" />
          <p>No hay videos todavía.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((item) => (
            <div key={item.id} className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0F1D33]/50 p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#60A5FA]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6 text-[#60A5FA]" fill="currentColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs uppercase bg-[#60A5FA]/20 text-[#60A5FA] px-2 py-0.5 rounded">{item.video_type}</span>
                  </div>
                  <p className="text-white font-medium truncate">{item.title}</p>
                  <p className="text-white/40 text-xs truncate mt-1">{item.video_url}</p>
                  {item.description && <p className="text-white/50 text-sm mt-2">{item.description}</p>}
                </div>
                <button
                  onClick={() => handleDelete(item.id)}
                  data-testid={`delete-video-${item.id}`}
                  className="w-8 h-8 bg-red-500/90 rounded-lg flex items-center justify-center hover:bg-red-600 flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoManager;
